import React, { useEffect, useState } from "react";
import api from "../utils/axiosConfig";
import { useNavigate } from "react-router-dom";
import { useCart } from "../components/Cart/CartContext";
import { motion } from "framer-motion";
import styled from "styled-components";
import { toast } from "react-toastify";
import MesCommandes from "../components/Commande/MesCommandes";
import MesFavoris from "../components/Favoris/mesFavoris";
import Footer from "../components/Footer";
import {
  FaUser,
  FaShoppingBag,
  FaHeart,
  FaSignOutAlt,
  FaCrown,
} from "react-icons/fa";

const ProfileContainer = styled(motion.div)`
  font-family: "Cormorant Garamond", serif;
  color: #2c1810;
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
  overflow-x: hidden;
  background: linear-gradient(135deg, #f8f5f0 0%, #f0ebe0 100%);

  @media (max-width: 768px) {
    padding: 1rem;
    margin: 0;
  }

  @media (max-width: 480px) {
    padding: 0.5rem;
  }
`;

const HeroSection = styled(motion.div)`
  position: relative;
  height: 60vh;
  min-height: 400px;
  background: linear-gradient(135deg, #8b5a2b 0%, #5a3921 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: white;
  text-align: center;
  margin-bottom: 80px;
  border-radius: 20px;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100vh;
    background: rgba(0, 0, 0, 0.3);
    backdrop-filter: blur(5px);
  }

  @media (max-width: 768px) {
    height: 50vh;
    min-height: 300px;
    margin-bottom: 40px;
    border-radius: 16px;
  }

  @media (max-width: 480px) {
    height: 40vh;
    min-height: 250px;
    margin-bottom: 30px;
    border-radius: 12px;
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  padding: 0 20px;
`;

const HeroTitle = styled.h1`
  font-size: 3.5rem;
  font-weight: 600;
  margin-bottom: 20px;
  letter-spacing: 1px;
  text-shadow: 2px 2px 8px rgba(0, 0, 0, 0.6);
  font-family: "Cormorant Garamond", serif;

  @media (max-width: 768px) {
    font-size: 2.5rem;
    margin-bottom: 15px;
  }

  @media (max-width: 480px) {
    font-size: 2rem;
    margin-bottom: 10px;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.3rem;
  font-style: italic;
  text-shadow: 1px 1px 4px rgba(0, 0, 0, 0.5);
  font-family: "Cormorant Garamond", serif;

  @media (max-width: 768px) {
    font-size: 1.1rem;
  }

  @media (max-width: 480px) {
    font-size: 1rem;
  }
`;

const TabsContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 3rem;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(15px);
  padding: 1rem;
  border-radius: 20px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(212, 175, 55, 0.2);

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.5rem;
    margin-bottom: 2rem;
    padding: 0.75rem;
  }

  @media (max-width: 480px) {
    margin-bottom: 1.5rem;
    padding: 0.5rem;
    border-radius: 12px;
  }
`;

const TabButton = styled(motion.button)`
  padding: 1rem 2rem;
  font-size: 1.1rem;
  font-weight: 600;
  background: ${({ active }) =>
    active ? "rgba(212, 175, 55, 0.3)" : "rgba(255, 255, 255, 0.1)"};
  border: 1px solid
    ${({ active }) =>
      active ? "rgba(212, 175, 55, 0.5)" : "rgba(212, 175, 55, 0.2)"};
  border-radius: 15px;
  color: ${({ active }) => (active ? "#2c1810" : "#2c1810")};
  cursor: pointer;
  transition: all 0.3s ease;
  margin: 0 0.5rem;
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: "Cormorant Garamond", serif;
  backdrop-filter: blur(10px);

  &:hover {
    background: ${({ active }) =>
      active ? "rgba(212, 175, 55, 0.4)" : "rgba(212, 175, 55, 0.2)"};
    transform: translateY(-2px);
    box-shadow: 0 4px 16px rgba(212, 175, 55, 0.2);
  }

  @media (max-width: 768px) {
    padding: 0.75rem 1.5rem;
    font-size: 1rem;
    margin: 0;
    justify-content: center;
  }

  @media (max-width: 480px) {
    padding: 0.6rem 1rem;
    font-size: 0.9rem;
    gap: 6px;
  }
`;

const ProfileCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(15px);
  padding: 3rem;
  border-radius: 20px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  margin-bottom: 2rem;
  border: 1px solid rgba(212, 175, 55, 0.3);
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    right: 0;
    width: 30%;
    height: 100%;
    background: rgba(212, 175, 55, 0.05);
  }

  @media (max-width: 768px) {
    padding: 2rem;
    margin-bottom: 1.5rem;
    border-radius: 16px;
  }

  @media (max-width: 480px) {
    padding: 1.5rem;
    margin-bottom: 1rem;
    border-radius: 12px;
  }
`;

const ProfileHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 2rem;
`;

const ProfileIcon = styled.div`
  width: 60px;
  height: 60px;
  background: rgba(212, 175, 55, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #2c1810;
  font-size: 1.5rem;
  border: 1px solid rgba(212, 175, 55, 0.3);
  backdrop-filter: blur(10px);
`;

const ProfileTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 600;
  color: #2c1810;
  margin: 0;
  font-family: "Cormorant Garamond", serif;

  @media (max-width: 768px) {
    font-size: 2rem;
  }

  @media (max-width: 480px) {
    font-size: 1.5rem;
  }
`;

const ProfileInfo = styled.div`
  position: relative;
  z-index: 2;
`;

const InfoItem = styled(motion.div)`
  display: flex;
  align-items: center;
  padding: 1rem 0;
  border-bottom: 1px solid rgba(212, 175, 55, 0.2);

  &:last-child {
    border-bottom: none;
  }

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
    padding: 0.75rem 0;
  }
`;

const InfoLabel = styled.span`
  font-weight: 600;
  color: #2c1810;
  min-width: 120px;
  font-size: 1.1rem;
  font-family: "Cormorant Garamond", serif;
`;

const InfoValue = styled.span`
  color: #5a4a3a;
  font-size: 1.1rem;
  margin-left: 1rem;
  font-family: "Cormorant Garamond", serif;
  
  @media (max-width: 480px) {
    font-size: 0.9rem;
    margin-left: 0.5rem;
    word-break: break-word;
    max-width: 200px;
  }
`;

const InfoInput = styled.input`
  color: #5a4a3a;
  font-size: 1.1rem;
  margin-left: 1rem;
  font-family: "Cormorant Garamond", serif;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(212, 175, 55, 0.3);
  border-radius: 8px;
  padding: 0.5rem;
  backdrop-filter: blur(10px);
  width: 250px;
  
  &:focus {
    outline: none;
    border-color: rgba(212, 175, 55, 0.6);
    box-shadow: 0 0 0 2px rgba(212, 175, 55, 0.2);
  }
  
  @media (max-width: 480px) {
    font-size: 0.9rem;
    margin-left: 0.5rem;
    width: 100%;
    max-width: 200px;
  }
`;

const InfoSelect = styled.select`
  color: #5a4a3a;
  font-size: 1.1rem;
  margin-left: 1rem;
  font-family: "Cormorant Garamond", serif;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(212, 175, 55, 0.3);
  border-radius: 8px;
  padding: 0.5rem;
  backdrop-filter: blur(10px);
  width: 250px;
  cursor: pointer;
  
  &:focus {
    outline: none;
    border-color: rgba(212, 175, 55, 0.6);
    box-shadow: 0 0 0 2px rgba(212, 175, 55, 0.2);
  }
  
  @media (max-width: 480px) {
    font-size: 0.9rem;
    margin-left: 0.5rem;
    width: 100%;
    max-width: 200px;
  }
`;

const InfoTextarea = styled.textarea`
  color: #5a4a3a;
  font-size: 1.1rem;
  margin-left: 1rem;
  font-family: "Cormorant Garamond", serif;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(212, 175, 55, 0.3);
  border-radius: 8px;
  padding: 0.5rem;
  backdrop-filter: blur(10px);
  width: 250px;
  min-height: 80px;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: rgba(212, 175, 55, 0.6);
    box-shadow: 0 0 0 2px rgba(212, 175, 55, 0.2);
  }
  
  @media (max-width: 480px) {
    font-size: 0.9rem;
    margin-left: 0.5rem;
    width: 100%;
    max-width: 200px;
  }
`;

const EditButton = styled(motion.button)`
  margin-left: 1rem;
  padding: 0.5rem 1rem;
  background: rgba(212, 175, 55, 0.2);
  color: #2c1810;
  border: 1px solid rgba(212, 175, 55, 0.3);
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: "Cormorant Garamond", serif;
  backdrop-filter: blur(10px);

  &:hover {
    background: rgba(212, 175, 55, 0.3);
    transform: translateY(-2px);
    box-shadow: 0 4px 16px rgba(212, 175, 55, 0.3);
  }
  
  @media (max-width: 480px) {
    margin-left: 0.5rem;
    margin-top: 0.5rem;
  }
`;

const SaveButton = styled(motion.button)`
  margin-left: 1rem;
  padding: 0.5rem 1rem;
  background: rgba(76, 175, 80, 0.2);
  color: #2c1810;
  border: 1px solid rgba(76, 175, 80, 0.3);
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: "Cormorant Garamond", serif;
  backdrop-filter: blur(10px);

  &:hover {
    background: rgba(76, 175, 80, 0.3);
    transform: translateY(-2px);
    box-shadow: 0 4px 16px rgba(76, 175, 80, 0.3);
  }
  
  @media (max-width: 480px) {
    margin-left: 0.5rem;
    margin-top: 0.5rem;
  }
`;

const CancelButton = styled(motion.button)`
  margin-left: 0.5rem;
  padding: 0.5rem 1rem;
  background: rgba(158, 158, 158, 0.2);
  color: #2c1810;
  border: 1px solid rgba(158, 158, 158, 0.3);
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: "Cormorant Garamond", serif;
  backdrop-filter: blur(10px);

  &:hover {
    background: rgba(158, 158, 158, 0.3);
    transform: translateY(-2px);
    box-shadow: 0 4px 16px rgba(158, 158, 158, 0.3);
  }
  
  @media (max-width: 480px) {
    margin-left: 0.5rem;
    margin-top: 0.5rem;
  }
`;

const LogoutButton = styled(motion.button)`
  margin-top: 2rem;
  padding: 1rem 2rem;
  background: rgba(212, 175, 55, 0.2);
  color: #2c1810;
  border: 1px solid rgba(212, 175, 55, 0.3);
  border-radius: 15px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 10px;
  transition: all 0.3s ease;
  font-family: "Cormorant Garamond", serif;
  backdrop-filter: blur(10px);
  text-transform: uppercase;
  letter-spacing: 0.5px;

  &:hover {
    background: rgba(212, 175, 55, 0.3);
    transform: translateY(-2px);
    box-shadow: 0 4px 16px rgba(212, 175, 55, 0.3);
  }
`;

const LoadingContainer = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 60vh;
  color: #2c1810;
`;

const LoadingText = styled.h2`
  font-size: 2rem;
  font-weight: 600;
  margin-bottom: 1rem;
  font-family: "Cormorant Garamond", serif;
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid rgba(212, 175, 55, 0.2);
  border-top: 3px solid #d4af37;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const ErrorContainer = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 60vh;
  color: #d32f2f;
  text-align: center;
`;

const ErrorText = styled.h2`
  font-size: 2rem;
  font-weight: 600;
  margin-bottom: 1rem;
  font-family: "Cormorant Garamond", serif;
`;

const ErrorIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 1rem;
`;

const TabContent = styled(motion.div)`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(15px);
  border-radius: 20px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  border: 1px solid rgba(212, 175, 55, 0.2);
`;

// Styles pour les modes d'affichage des favoris
const FavoritesViewControls = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-bottom: 2rem;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(15px);
  border-radius: 20px;
  border: 1px solid rgba(212, 175, 55, 0.2);

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.5rem;
    margin-bottom: 1.5rem;
    padding: 0.75rem;
  }

  @media (max-width: 480px) {
    margin-bottom: 1rem;
    padding: 0.5rem;
    border-radius: 8px;
  }
`;

const ViewModeButton = styled.button`
  padding: 0.5rem 1rem;
  border: 1px solid
    ${({ active }) =>
      active ? "rgba(212, 175, 55, 0.5)" : "rgba(212, 175, 55, 0.2)"};
  background: ${({ active }) =>
    active ? "rgba(212, 175, 55, 0.3)" : "rgba(255, 255, 255, 0.1)"};
  color: ${({ active }) => (active ? "#2c1810" : "#2c1810")};
  border-radius: 15px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  backdrop-filter: blur(10px);
  font-family: "Cormorant Garamond", serif;

  &:hover {
    border-color: rgba(212, 175, 55, 0.5);
    background: ${({ active }) =>
      active ? "rgba(212, 175, 55, 0.4)" : "rgba(212, 175, 55, 0.2)"};
    transform: translateY(-2px);
    box-shadow: 0 4px 16px rgba(212, 175, 55, 0.2);
  }

  @media (max-width: 768px) {
    padding: 0.75rem 1rem;
    justify-content: center;
  }

  @media (max-width: 480px) {
    padding: 0.6rem 0.8rem;
    font-size: 0.9rem;
  }
`;

const FavoritesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1.5rem;
  padding: 1.5rem;

  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
    gap: 1rem;
    padding: 1rem;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 0.75rem;
    padding: 0.75rem;
  }
`;

const FavoritesHorizontal = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1.5rem;
`;

const FavoritesVertical = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1.5rem;
`;

const FavoriteCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(15px);
  border-radius: 20px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  transition: all 0.3s ease;
  cursor: pointer;
  border: 1px solid rgba(212, 175, 55, 0.2);
  position: relative;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, #d4af37, #2c1810, #d4af37);
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 25px rgba(212, 175, 55, 0.2);
    border-color: rgba(212, 175, 55, 0.3);

    &::before {
      opacity: 1;
    }
  }
`;

const FavoriteCardHorizontal = styled(FavoriteCard)`
  display: flex;
  align-items: stretch;
  padding: 1.5rem;
  gap: 1.5rem;
  min-height: 160px;

  @media (max-width: 768px) {
    flex-direction: column;
    padding: 1rem;
    gap: 1rem;
    min-height: auto;
  }

  @media (max-width: 480px) {
    padding: 0.75rem;
    gap: 0.75rem;
  }
`;

const FavoriteCardVertical = styled(FavoriteCard)`
  display: flex;
  flex-direction: column;
  padding: 0;
  height: 100%;
`;

const FavoriteImage = styled.div`
  width: 120px;
  height: 120px;
  border-radius: 15px;
  flex-shrink: 0;
  position: relative;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(212, 175, 55, 0.2);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;
  }

  &:hover img {
    transform: scale(1.05);
  }

  @media (max-width: 768px) {
    width: 100%;
    height: 200px;
  }

  @media (max-width: 480px) {
    height: 150px;
  }
`;

const FavoriteImageVertical = styled.div`
  width: 100%;
  height: 220px;
  position: relative;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;
  }

  &:hover img {
    transform: scale(1.05);
  }

  @media (max-width: 768px) {
    height: 180px;
  }

  @media (max-width: 480px) {
    height: 150px;
  }
`;

const FavoriteInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  justify-content: space-between;
`;

const FavoriteHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
`;

const FavoriteTitle = styled.h3`
  font-size: 1.2rem;
  font-weight: 600;
  color: #2c1810;
  margin: 0;
  line-height: 1.3;
  flex: 1;
  font-family: "Cormorant Garamond", serif;

  @media (max-width: 768px) {
    font-size: 1.1rem;
  }

  @media (max-width: 480px) {
    font-size: 1rem;
  }
`;

const FavoriteBadge = styled.span`
  background: rgba(212, 175, 55, 0.2);
  color: #2c1810;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: 0 2px 8px rgba(212, 175, 55, 0.2);
  border: 1px solid rgba(212, 175, 55, 0.3);
`;

const FavoritePrice = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

const PriceAmount = styled.span`
  font-size: 1.3rem;
  font-weight: 600;
  color: #d4af37;
  font-family: "Cormorant Garamond", serif;
`;

const PriceCurrency = styled.span`
  font-size: 0.9rem;
  font-weight: 500;
  color: #718096;
`;

const FavoriteDetails = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-top: 0.5rem;
`;

const DetailTag = styled.span`
  background: rgba(212, 175, 55, 0.1);
  color: #2c1810;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 500;
  border: 1px solid rgba(212, 175, 55, 0.2);
  font-family: "Cormorant Garamond", serif;
`;

const FavoriteActions = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 1rem;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.75rem;
  }

  @media (max-width: 480px) {
    gap: 0.5rem;
  }
`;

const ActionButton = styled(motion.button)`
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ViewButton = styled(ActionButton)`
  background: rgba(212, 175, 55, 0.2);
  color: #2c1810;
  border: 1px solid rgba(212, 175, 55, 0.3);
  flex: 1;
  backdrop-filter: blur(10px);
  font-family: "Cormorant Garamond", serif;

  &:hover {
    background: rgba(212, 175, 55, 0.3);
    transform: translateY(-2px);
    box-shadow: 0 4px 16px rgba(212, 175, 55, 0.3);
  }
`;

const RemoveButton = styled(ActionButton)`
  background: rgba(212, 175, 55, 0.1);
  color: #d32f2f;
  border: 1px solid rgba(212, 175, 55, 0.2);
  backdrop-filter: blur(10px);
  font-family: "Cormorant Garamond", serif;

  &:hover {
    background: rgba(212, 175, 55, 0.2);
    transform: translateY(-2px);
    box-shadow: 0 4px 16px rgba(212, 175, 55, 0.2);
  }
`;

const FavoriteDescription = styled.p`
  font-size: 0.9rem;
  color: #5a4a3a;
  line-height: 1.5;
  margin: 0.5rem 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  font-family: "Cormorant Garamond", serif;
`;

const FavoriteStats = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid rgba(212, 175, 55, 0.2);
`;

const StatItem = styled.div`
  text-align: center;
`;

const StatValue = styled.div`
  font-size: 1.1rem;
  font-weight: 600;
  color: #d4af37;
  font-family: "Cormorant Garamond", serif;
`;

const StatLabel = styled.div`
  font-size: 0.75rem;
  color: #5a4a3a;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-family: "Cormorant Garamond", serif;
`;

const EmptyFavorites = styled.div`
  text-align: center;
  padding: 3rem;
  color: #5a4a3a;
  font-family: "Cormorant Garamond", serif;
`;

const EmptyFavoritesIcon = styled.div`
  font-size: 3rem;
  color: #d4af37;
  margin-bottom: 1rem;
`;

// Composant pour afficher les favoris avec différents modes
const FavoritesView = ({ viewMode, setViewMode }) => {
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";

  useEffect(() => {
    const fetchFavorites = async () => {
      try {
        const res = await api.get(`${apiUrl}/api/favorites`, {
          withCredentials: true,
        });
        setFavorites(res.data.favorites || []);
      } catch (error) {
        console.error("Erreur lors de la récupération des favoris :", error);
      } finally {
        setLoading(false);
      }
    };

    fetchFavorites();
  }, [apiUrl]);

  const buildImageUrl = (filename) => {
  if (!filename || typeof filename !== "string") return "";

  // URL complète détectée, on la retourne brute
  if (/^https?:\/\//i.test(filename)) {
    return filename;
  }

  // Sinon on construit URL locale
  let cleanFilename = filename.startsWith("/") ? filename.slice(1) : filename;

  if (cleanFilename.startsWith("public/")) {
    cleanFilename = cleanFilename.slice("public/".length);
  }

  if (!cleanFilename.startsWith("uploads/")) {
    cleanFilename = `uploads/${cleanFilename}`;
  }

  return `${apiUrl}/${cleanFilename}`.replace(/([^:]\/)\/+/g, "$1");
};

  const handleFavoriteClick = (productId) => {
    navigate(`/product/${productId}`);
  };

  if (loading) {
    return (
      <div style={{ padding: "2rem", textAlign: "center" }}>
        <div
          style={{
            width: "40px",
            height: "40px",
            border: "3px solid transparent",
            borderTop: "3px solid #8b5a2b",
            borderRadius: "50%",
            animation: "spin 1s linear infinite",
            margin: "0 auto 1rem",
          }}
        ></div>
        <p>Chargement de vos favoris...</p>
      </div>
    );
  }

  if (favorites.length === 0) {
    return (
      <EmptyFavorites>
        <EmptyFavoritesIcon>💔</EmptyFavoritesIcon>
        <h3>Aucun favori</h3>
        <p>Vous n'avez pas encore ajouté de produits à vos favoris.</p>
      </EmptyFavorites>
    );
  }

  const renderFavoriteCard = (favorite) => {
    const { product, couleur = "", taille = "" } = favorite;
    if (!product) return null;

    const firstImage = product.variantes?.[0]?.images?.[0];
    const imageUrl = firstImage
      ? buildImageUrl(firstImage)
      : "https://via.placeholder.com/300x300/cccccc/ffffff?text=Produit";

    const handleRemoveFavorite = async (e) => {
      e.stopPropagation();
      try {
        await api.delete(`${apiUrl}/api/favorites/${product._id}`, {
          withCredentials: true,
        });
        setFavorites(
          favorites.filter((fav) => fav.product._id !== product._id)
        );
      } catch (error) {
        console.error("Erreur lors de la suppression du favori :", error);
      }
    };

    if (viewMode === "horizontal") {
      return (
        <FavoriteCardHorizontal key={product._id} whileHover={{ scale: 1.02 }}>
          <FavoriteImage>
            <img src={imageUrl} alt={product.name} />
          </FavoriteImage>
          <FavoriteInfo>
            <FavoriteHeader>
              <FavoriteTitle>{product.name}</FavoriteTitle>
              <FavoriteBadge>Favori</FavoriteBadge>
            </FavoriteHeader>

            <FavoriteDescription>
              {product.description ||
                "Produit artisanal marocain de qualité premium"}
            </FavoriteDescription>

            <FavoritePrice>
              <PriceAmount>
                {product.price.promoPrice != null
                  ? product.price.promoPrice.toFixed(2)
                  : product.price.originalPrice != null
                  ? product.price.originalPrice.toFixed(2)
                  : "—"}
              </PriceAmount>
              <PriceCurrency>MAD</PriceCurrency>
            </FavoritePrice>

            <FavoriteDetails>
              {couleur && <DetailTag>🎨 {couleur}</DetailTag>}
              {taille && <DetailTag>📏 {taille}</DetailTag>}
              <DetailTag>🏷️ {product.category || "Babouche"}</DetailTag>
            </FavoriteDetails>

            <FavoriteActions>
              <ViewButton
                onClick={() => handleFavoriteClick(product._id)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                👁️ Voir
              </ViewButton>
              <RemoveButton
                onClick={handleRemoveFavorite}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                ❌ Retirer
              </RemoveButton>
            </FavoriteActions>
          </FavoriteInfo>
        </FavoriteCardHorizontal>
      );
    }

    if (viewMode === "vertical") {
      return (
        <FavoriteCardVertical key={product._id} whileHover={{ scale: 1.02 }}>
          <FavoriteImageVertical>
            <img src={imageUrl} alt={product.name} />
          </FavoriteImageVertical>
          <div
            style={{
              padding: "1.5rem",
              flex: 1,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <FavoriteHeader>
              <FavoriteTitle>{product.name}</FavoriteTitle>
              <FavoriteBadge>Favori</FavoriteBadge>
            </FavoriteHeader>

            <FavoriteDescription>
              {product.description ||
                "Produit artisanal marocain de qualité premium"}
            </FavoriteDescription>

            <FavoritePrice>
              <PriceAmount>
                {product.price.promoPrice != null
                  ? product.price.promoPrice.toFixed(2)
                  : product.price.originalPrice != null
                  ? product.price.originalPrice.toFixed(2)
                  : "—"}
              </PriceAmount>
              <PriceCurrency>MAD</PriceCurrency>
            </FavoritePrice>

            <FavoriteDetails>
              {couleur && <DetailTag>🎨 {couleur}</DetailTag>}
              {taille && <DetailTag>📏 {taille}</DetailTag>}
              <DetailTag>🏷️ {product.category || "Babouche"}</DetailTag>
            </FavoriteDetails>

            <FavoriteStats>
              <StatItem>
                <StatValue>⭐</StatValue>
                <StatLabel>Premium</StatLabel>
              </StatItem>
              <StatItem>
                <StatValue>🛡️</StatValue>
                <StatLabel>Garantie</StatLabel>
              </StatItem>
              <StatItem>
                <StatValue>🚚</StatValue>
                <StatLabel>Livraison</StatLabel>
              </StatItem>
            </FavoriteStats>

            <FavoriteActions>
              <ViewButton
                onClick={() => handleFavoriteClick(product._id)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                👁️ Voir le produit
              </ViewButton>
              <RemoveButton
                onClick={handleRemoveFavorite}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                ❌ Retirer
              </RemoveButton>
            </FavoriteActions>
          </div>
        </FavoriteCardVertical>
      );
    }

    // Mode grid (colonnes)
    return (
      <FavoriteCard key={product._id} whileHover={{ scale: 1.02 }}>
        <FavoriteImageVertical>
          <img src={imageUrl} alt={product.name} />
        </FavoriteImageVertical>
        <div
          style={{
            padding: "1.5rem",
            flex: 1,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <FavoriteHeader>
            <FavoriteTitle>{product.name}</FavoriteTitle>
            <FavoriteBadge>Favori</FavoriteBadge>
          </FavoriteHeader>

          <FavoriteDescription>
            {product.description ||
              "Produit artisanal marocain de qualité premium"}
          </FavoriteDescription>

          <FavoritePrice>
            <PriceAmount>
              {product.price.promoPrice != null
                ? product.price.promoPrice.toFixed(2)
                : product.price.originalPrice != null
                ? product.price.originalPrice.toFixed(2)
                : "—"}
            </PriceAmount>
            <PriceCurrency>MAD</PriceCurrency>
          </FavoritePrice>

          <FavoriteDetails>
            {couleur && <DetailTag>🎨 {couleur}</DetailTag>}
            {taille && <DetailTag>📏 {taille}</DetailTag>}
            <DetailTag>🏷️ {product.category || "Babouche"}</DetailTag>
          </FavoriteDetails>

          <FavoriteStats>
            <StatItem>
              <StatValue>⭐</StatValue>
              <StatLabel>Premium</StatLabel>
            </StatItem>
            <StatItem>
              <StatValue>🛡️</StatValue>
              <StatLabel>Garantie</StatLabel>
            </StatItem>
            <StatItem>
              <StatValue>🚚</StatValue>
              <StatLabel>Livraison</StatLabel>
            </StatItem>
          </FavoriteStats>

          <FavoriteActions>
            <ViewButton
              onClick={() => handleFavoriteClick(product._id)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              👁️ Voir
            </ViewButton>
            <RemoveButton
              onClick={handleRemoveFavorite}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              ❌ Retirer
            </RemoveButton>
          </FavoriteActions>
        </div>
      </FavoriteCard>
    );
  };

  return (
    <div>
      <FavoritesViewControls>
        <ViewModeButton
          active={viewMode === "grid"}
          onClick={() => setViewMode("grid")}
        >
          <span>⊞</span>
          Grille
        </ViewModeButton>
        <ViewModeButton
          active={viewMode === "horizontal"}
          onClick={() => setViewMode("horizontal")}
        >
          <span>☰</span>
          Horizontal
        </ViewModeButton>
        <ViewModeButton
          active={viewMode === "vertical"}
          onClick={() => setViewMode("vertical")}
        >
          <span>☰</span>
          Vertical
        </ViewModeButton>
      </FavoritesViewControls>

      {viewMode === "grid" && (
        <FavoritesGrid>{favorites.map(renderFavoriteCard)}</FavoritesGrid>
      )}

      {viewMode === "horizontal" && (
        <FavoritesHorizontal>
          {favorites.map(renderFavoriteCard)}
        </FavoritesHorizontal>
      )}

      {viewMode === "vertical" && (
        <FavoritesVertical>
          {favorites.map(renderFavoriteCard)}
        </FavoritesVertical>
      )}
    </div>
  );
};

export default function Profile() {
  const [profile, setProfile] = useState(null);
  const [activeTab, setActiveTab] = useState("profile");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [favoritesViewMode, setFavoritesViewMode] = useState("grid"); // "grid", "horizontal", "vertical"
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const navigate = useNavigate();
  const { resetCart } = useCart();

  // Liste des villes du Maroc classées par popularité
  const villesMaroc = [
    // Grandes métropoles
    "Casablanca",
    "Rabat",
    "Fès",
    "Marrakech",
    "Agadir",
    "Tanger",
    "Meknès",
    "Oujda",
    "Kénitra",
    "Tétouan",
    
    // Villes importantes
    "Safi",
    "El Jadida",
    "Béni Mellal",
    "Taza",
    "Larache",
    "Khouribga",
    "Settat",
    "Berkane",
    "Taourirt",
    "Nador",
    
    // Villes touristiques
    "Essaouira",
    "Chefchaouen",
    "Ifrane",
    "Azrou",
    "Midelt",
    "Errachidia",
    "Ouarzazate",
    "Zagora",
    "Tinghir",
    "Merzouga",
    
    // Autres villes
    "Dakhla",
    "Laâyoune",
    "Guelmim",
    "Tan-Tan",
    "Tarfaya",
    "Boujdour",
    "Smara",
    "Es-Semara",
    "Aousserd",
    "Bouarfa"
  ];

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await api.get('/api/auth/profile');
        const profileData = res.data.profile;
        setProfile(profileData);
        
        // Initialiser les données d'édition avec les valeurs actuelles
        setEditData({
          name: profileData.name || '',
          email: profileData.email || '',
          phone: profileData.phone || '',
          address: profileData.address || '',
          city: profileData.city || 'Casablanca',
          country: profileData.country || 'Maroc'
        });
      } catch (err) {
        console.error("Erreur récupération profil :", err);
        setError("Session expirée ou accès interdit.");
        resetCart();
        navigate("/login", { replace: true });
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [navigate, resetCart]);

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    // Restaurer les données originales
    setEditData({
      name: profile.name || '',
      email: profile.email || '',
      phone: profile.phone || '',
      address: profile.address || '',
      city: profile.city || 'Casablanca',
      country: profile.country || 'Maroc'
    });
  };

  const handleSave = async () => {
    try {
      const res = await api.put('/api/auth/profile', editData);
      setProfile(res.data.profile);
      setIsEditing(false);
      
      toast.success(
        "✅ Profil mis à jour avec succès !",
        {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          style: {
            fontFamily: "'Cormorant Garamond', serif",
            fontSize: "14px",
            borderRadius: "12px",
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
            background: "rgba(255, 255, 255, 0.95)",
            backdropFilter: "blur(10px)",
            border: "1px solid rgba(76, 175, 80, 0.3)",
            color: "#2c1810",
          }
        }
      );
    } catch (err) {
      console.error("Erreur mise à jour profil :", err);
      toast.error(
        "❌ Erreur lors de la mise à jour du profil",
        {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          style: {
            fontFamily: "'Cormorant Garamond', serif",
            fontSize: "14px",
            borderRadius: "12px",
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
            background: "rgba(255, 255, 255, 0.95)",
            backdropFilter: "blur(10px)",
            border: "1px solid rgba(239, 68, 68, 0.3)",
            color: "#2c1810",
          }
        }
      );
    }
  };

  const handleInputChange = (field, value) => {
    setEditData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const signOut = async () => {
    try {
      await api.post('/api/auth/logout', {});
      
      // Notification de déconnexion réussie
      toast.success(
        "👋 Vous avez été déconnecté avec succès !\nMerci de votre visite sur Babouche Commerce",
        {
          position: "top-right",
          autoClose: 4000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          style: {
            fontFamily: "'Cormorant Garamond', serif",
            fontSize: "14px",
            borderRadius: "12px",
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
            background: "rgba(255, 255, 255, 0.95)",
            backdropFilter: "blur(10px)",
            border: "1px solid rgba(212, 175, 55, 0.3)",
            color: "#2c1810",
          }
        }
      );
      
      // Nettoyer les stockages et rediriger immédiatement
      try {
        localStorage.clear();
        sessionStorage.clear();
      } catch (e) {}
      resetCart();
      navigate("/", { replace: true });
      
    } catch (err) {
      console.error("Erreur lors de la déconnexion", err);
      
      // Notification d'erreur de déconnexion
      toast.error(
        "Erreur lors de la déconnexion. Vous allez être redirigé.",
        {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          style: {
            fontFamily: "'Cormorant Garamond', serif",
            fontSize: "14px",
            borderRadius: "12px",
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
            background: "rgba(255, 255, 255, 0.95)",
            backdropFilter: "blur(10px)",
            border: "1px solid rgba(239, 68, 68, 0.3)",
            color: "#2c1810",
          }
        }
      );
      
      resetCart();
      navigate("/login");
    }
  };

  if (loading) {
    return (
      <LoadingContainer
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <LoadingSpinner />
        <LoadingText>Chargement de votre profil...</LoadingText>
      </LoadingContainer>
    );
  }

  if (error) {
    return (
      <ErrorContainer
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        <ErrorIcon>⚠️</ErrorIcon>
        <ErrorText>{error}</ErrorText>
      </ErrorContainer>
    );
  }

  if (!profile) return null;

  return (
    <>
      <ProfileContainer
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        <div
          style={{ height: window.innerWidth <= 768 ? "7vh" : "15vh" }}
        ></div>

        <TabsContainer
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.6 }}
        >
          <TabButton
            active={activeTab === "profile"}
            onClick={() => setActiveTab("profile")}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <FaUser />
            Profil
          </TabButton>
          <TabButton
            active={activeTab === "orders"}
            onClick={() => setActiveTab("orders")}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <FaShoppingBag />
            Mes Commandes
          </TabButton>
          <TabButton
            active={activeTab === "favorites"}
            onClick={() => setActiveTab("favorites")}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <FaHeart />
            Mes Favoris
          </TabButton>
        </TabsContainer>

        {activeTab === "profile" && (
          <ProfileCard
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <ProfileHeader>
              <ProfileIcon>
                <FaCrown />
              </ProfileIcon>
              <ProfileTitle>Informations personnelles</ProfileTitle>
              {!isEditing && (
                <EditButton
                  onClick={handleEdit}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  ✏️ Modifier
                </EditButton>
              )}
            </ProfileHeader>

            <ProfileInfo>
              <InfoItem
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.2 }}
              >
                <InfoLabel>Nom :</InfoLabel>
                {isEditing ? (
                  <InfoInput
                    type="text"
                    value={editData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder="Votre nom"
                  />
                ) : (
                  <InfoValue>{profile.name}</InfoValue>
                )}
              </InfoItem>

              <InfoItem
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.3 }}
              >
                <InfoLabel>Email :</InfoLabel>
                {isEditing ? (
                  <InfoInput
                    type="email"
                    value={editData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    placeholder="Votre email"
                  />
                ) : (
                  <InfoValue>{profile.email}</InfoValue>
                )}
              </InfoItem>

              <InfoItem
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.4 }}
              >
                <InfoLabel>Téléphone :</InfoLabel>
                {isEditing ? (
                  <InfoInput
                    type="tel"
                    value={editData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    placeholder="Votre numéro de téléphone"
                  />
                ) : (
                  <InfoValue>{profile.phone || "Non renseigné"}</InfoValue>
                )}
              </InfoItem>

              <InfoItem
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.5 }}
              >
                <InfoLabel>Adresse :</InfoLabel>
                {isEditing ? (
                  <InfoTextarea
                    value={editData.address}
                    onChange={(e) => handleInputChange('address', e.target.value)}
                    placeholder="Votre adresse de livraison complète"
                  />
                ) : (
                  <InfoValue>{profile.address || "Non renseignée"}</InfoValue>
                )}
              </InfoItem>

              <InfoItem
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.6 }}
              >
                <InfoLabel>Ville :</InfoLabel>
                {isEditing ? (
                  <InfoSelect
                    value={editData.city}
                    onChange={(e) => handleInputChange('city', e.target.value)}
                  >
                    {villesMaroc.map((ville) => (
                      <option key={ville} value={ville}>
                        {ville}
                      </option>
                    ))}
                  </InfoSelect>
                ) : (
                  <InfoValue>{profile.city || "Non renseignée"}</InfoValue>
                )}
              </InfoItem>

              <InfoItem
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.7 }}
              >
                <InfoLabel>Pays :</InfoLabel>
                {isEditing ? (
                  <InfoInput
                    type="text"
                    value={editData.country}
                    onChange={(e) => handleInputChange('country', e.target.value)}
                    placeholder="Maroc"
                    readOnly
                  />
                ) : (
                  <InfoValue>{profile.country || "Maroc"}</InfoValue>
                )}
              </InfoItem>
            </ProfileInfo>

            {isEditing ? (
              <div style={{ display: 'flex', gap: '1rem', marginTop: '2rem' }}>
                <SaveButton
                  onClick={handleSave}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  💾 Enregistrer
                </SaveButton>
                <CancelButton
                  onClick={handleCancel}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  ❌ Annuler
                </CancelButton>
              </div>
            ) : (
              <LogoutButton
                onClick={signOut}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <FaSignOutAlt />
                Se déconnecter
              </LogoutButton>
            )}
          </ProfileCard>
        )}

        {(activeTab === "orders" || activeTab === "favorites") && (
          <TabContent
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {activeTab === "orders" && <MesCommandes />}
            {activeTab === "favorites" && (
              <FavoritesView
                viewMode={favoritesViewMode}
                setViewMode={setFavoritesViewMode}
              />
            )}
          </TabContent>
        )}
      </ProfileContainer>

      <Footer />
    </>
  );
}
